import React, { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDay } from "@/lib/api";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, DollarSign, AlertTriangle, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { downloadReport } from "@/lib/pdf";

export default function CostControlPage() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/cost-control/summary").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-10 text-zinc-500">Loading…</div>;

  const eventChart = (data.events || []).slice(0, 8).map((e) => ({
    name: e.customer_name.split(" ")[0],
    revenue: e.revenue,
    cost: e.est_cost,
    profit: e.est_profit,
  }));

  const exportPDF = () => {
    downloadReport({
      title: "Cost Control Report",
      filename: `Cost-Control-${new Date().toISOString().slice(0, 10)}.pdf`,
      kpis: [
        { label: "Total Revenue", value: fmtCurrency(data.total_revenue) },
        { label: "Purchasing", value: fmtCurrency(data.total_purchasing) },
        { label: "Waste Value", value: fmtCurrency(data.waste_value) },
        { label: "Food Cost %", value: `${data.food_cost_pct}%` },
      ],
      sections: [
        {
          title: "Per-Event Margin Breakdown",
          columns: ["Customer", "Event Type", "Date", "Revenue", "Est Cost", "Profit", "Margin %"],
          rows: (data.events || []).map((e) => [
            e.customer_name, e.event_type, fmtDay(e.event_date),
            fmtCurrency(e.revenue), fmtCurrency(e.est_cost),
            fmtCurrency(e.est_profit), `${e.margin_pct.toFixed(0)}%`,
          ]),
          columnStyles: { 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
        },
      ],
    });
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="cost-control-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Finance</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Cost Control</h1>
        </div>
        <Button onClick={exportPDF} data-testid="cost-pdf" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
          <Download className="w-4 h-4 mr-1.5" /> Download PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Tile icon={DollarSign} label="Total Revenue" value={fmtCurrency(data.total_revenue)} tint="#10B981" />
        <Tile icon={TrendingDown} label="Total Purchasing" value={fmtCurrency(data.total_purchasing)} tint="#F59E0B" />
        <Tile icon={AlertTriangle} label="Waste Value" value={fmtCurrency(data.waste_value)} tint="#EF4444" />
        <Tile icon={TrendingUp} label="Food Cost %" value={`${data.food_cost_pct}%`} tint={data.food_cost_pct < 35 ? "#10B981" : data.food_cost_pct < 50 ? "#F59E0B" : "#EF4444"} />
      </div>

      <div className="card-lux p-6 mb-6">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="label-eyebrow">Per-Event Margins</div>
            <h3 className="font-display text-xl mt-1">Revenue vs Estimated Cost</h3>
          </div>
          <div className="text-sm">
            <span className="text-zinc-500">Gross profit: </span>
            <span className="text-[#D4AF37] font-display text-lg">{fmtCurrency(data.gross_profit)}</span>
          </div>
        </div>
        <div style={{ height: 280 }} className="mt-4">
          {eventChart.length === 0 ? (
            <div className="h-full flex items-center justify-center text-zinc-600 text-sm">No event data</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={eventChart}>
                <CartesianGrid strokeDasharray="2 4" stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="name" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 4, fontSize: 12 }} />
                <Bar dataKey="revenue" fill="#D4AF37" radius={[3, 3, 0, 0]} />
                <Bar dataKey="cost" fill="#F59E0B" radius={[3, 3, 0, 0]} />
                <Bar dataKey="profit" fill="#10B981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="card-lux p-6">
        <div className="label-eyebrow">Event-by-Event</div>
        <h3 className="font-display text-xl mt-1 mb-4">Margin breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b border-[#1a1a1a] text-zinc-500"><th className="py-3 label-eyebrow text-[10px]">Customer</th><th className="py-3 label-eyebrow text-[10px]">Date</th><th className="py-3 label-eyebrow text-[10px] text-right">Revenue</th><th className="py-3 label-eyebrow text-[10px] text-right">Est Cost</th><th className="py-3 label-eyebrow text-[10px] text-right">Profit</th><th className="py-3 label-eyebrow text-[10px] text-right">Margin</th></tr></thead>
            <tbody>
              {data.events.map((e) => (
                <tr key={e.id} className="border-b border-[#1a1a1a]" data-testid={`cc-${e.id}`}>
                  <td className="py-3">
                    <Link to={`/orders/${e.id}`} className="text-zinc-200 hover:text-[#D4AF37]">{e.customer_name}</Link>
                    <div className="text-xs text-zinc-500 capitalize mt-0.5">{e.event_type}</div>
                  </td>
                  <td className="py-3 text-xs text-zinc-400">{fmtDay(e.event_date)}</td>
                  <td className="py-3 text-right">{fmtCurrency(e.revenue)}</td>
                  <td className="py-3 text-right text-amber-400">{fmtCurrency(e.est_cost)}</td>
                  <td className="py-3 text-right" style={{ color: e.est_profit > 0 ? "#10B981" : "#EF4444" }}>{fmtCurrency(e.est_profit)}</td>
                  <td className="py-3 text-right font-medium" style={{ color: e.margin_pct > 30 ? "#10B981" : e.margin_pct > 15 ? "#F59E0B" : "#EF4444" }}>{e.margin_pct.toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, tint }) {
  return (
    <div className="card-lux p-5">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        <div className="w-7 h-7 rounded-sm flex items-center justify-center" style={{ background: `${tint}15`, color: tint }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-display text-3xl mt-2" style={{ color: tint }}>{value}</div>
    </div>
  );
}
